/**
 * Übergangs-Effekt beim Theme- und Hell/Dunkel-Wechsel.
 *
 * WARUM ZENTRAL: Der Wechsel selbst liegt im ColorThemeProvider, also muss der
 * Effekt auch dorthin — sonst müsste ihn jede App einzeln nachbauen und die
 * Varianten liefen auseinander.
 *
 * WARUM MIT URSPRUNG: "wave" startet dort, wo der Zeiger zuletzt war. Damit ist
 * die Bewegung kausal — sie sagt "diese Änderung kam von diesem Schalter" —
 * statt bloß dekorativ zu sein. Der Umschalter sitzt am Desktop oben rechts und
 * mobil im Kopf oder im Fuss des Schubfachs; von dort liest es sich eindeutig.
 *
 * Die Zeigerposition wird global mitgeschrieben, damit die Aufrufer nichts
 * durchreichen müssen. Ist sie alt (> 2 s) oder gab es sie nie — Tastatur,
 * System-Dark-Mode, Profil-Sync, iframe-Bridge — fällt es auf eine Blende
 * zurück. Ohne diesen Fallback spränge die Welle aus Ecke (0,0) und sähe
 * schlicht kaputt aus.
 */

import { readThemeCookie, writeThemeCookie, THEME_TRANSITION_COOKIE } from "./theme-cookie";
import { prefersReducedMotion } from "./bewegung";
import { meldeThemaAnEinbettungen, THEMA_SOFORT } from "./thema-einbettungen";

/** `apply` und sofort die Einbettungen melden (ms 0); fuer Wege ohne Schnappschuss. */
function anwendenSofort(apply: () => void): void {
  apply();
  void meldeThemaAnEinbettungen(THEMA_SOFORT);
}

/**
 * Fuer den Update-Callback einer View-Transition: `apply`, dann auf die Einbettungen warten.
 * Ziel ms 0 — die Transition blendet das iframe als Teil der Seite mit, eine eigene
 * Ueberblendung darin liefe doppelt.
 */
function anwendenImSchnappschuss(apply: () => void): () => Promise<void> | undefined {
  return () => {
    apply();
    return meldeThemaAnEinbettungen(THEMA_SOFORT) ?? undefined;
  };
}

export type ThemeTransition = "none" | "soft" | "wave" | "diagonal";

export const THEME_TRANSITIONS: { id: ThemeTransition; label: string; hint: string }[] = [
  { id: "none", label: "Keine", hint: "Sofortiger Wechsel." },
  { id: "soft", label: "Weich", hint: "Farben blenden über." },
  { id: "wave", label: "Welle", hint: "Breitet sich vom Schalter aus." },
  { id: "diagonal", label: "Diagonal", hint: "Schräge Kante wischt durch." },
];

export const DEFAULT_THEME_TRANSITION: ThemeTransition = "wave";

/** localStorage-Key — exportiert fuer den storage-Listener (Same-Origin-Tabs). */
export const THEME_TRANSITION_LS_KEY = "janeway-theme-transition";
const LS_KEY = THEME_TRANSITION_LS_KEY;
/** Ab hier gilt die Zeigerposition als veraltet und der Ursprung als unbekannt. */
const POINTER_MAX_AGE_MS = 2000;

export function isThemeTransition(v: unknown): v is ThemeTransition {
  return v === "none" || v === "soft" || v === "wave" || v === "diagonal";
}

/**
 * Cookie hat Vorrang vor localStorage — GENAU wie beim Color-Theme.
 *
 * localStorage ist origin-gebunden: eine Wahl in ai-chat erreichte hub nie, dort
 * lief weiter der Default (Welle). Der geteilte Parent-Domain-Cookie ist der
 * einzige Speicher, den sich alle Subdomains teilen. Der localStorage-Wert bleibt
 * als Fallback fuer Kontexte ohne Cookie-Domain (lokale Entwicklung).
 */
export function readThemeTransition(): ThemeTransition {
  if (typeof window === "undefined") return DEFAULT_THEME_TRANSITION;
  const c = readThemeCookie(THEME_TRANSITION_COOKIE);
  if (isThemeTransition(c)) return c;
  const v = window.localStorage.getItem(LS_KEY);
  return isThemeTransition(v) ? v : DEFAULT_THEME_TRANSITION;
}

export function writeThemeTransition(v: ThemeTransition): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LS_KEY, v);
  writeThemeCookie(THEME_TRANSITION_COOKIE, v);
}

// ── Zeigerposition ─────────────────────────────────────────────────────

let lastPointer: { x: number; y: number; t: number } | null = null;
let tracking = false;

/** Einmalig pro Dokument. Idempotent — mehrere Provider-Instanzen sind ok. */
export function trackPointerOrigin(): void {
  if (tracking || typeof window === "undefined") return;
  tracking = true;
  window.addEventListener(
    "pointerdown",
    (e) => {
      lastPointer = { x: e.clientX, y: e.clientY, t: Date.now() };
    },
    { capture: true, passive: true },
  );
}

/** Mittelpunkt des auslösenden Schalters — exakt und unabhängig davon, wo
 *  innerhalb der Schaltfläche getippt wurde. Ein Element ohne Ausdehnung
 *  (ausgehängt, display:none) liefert 0×0 und wird verworfen. */
function elementOrigin(el?: Element | null): { x: number; y: number } | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

function freshPointer(): { x: number; y: number } | null {
  if (!lastPointer) return null;
  if (Date.now() - lastPointer.t > POINTER_MAX_AGE_MS) return null;
  return { x: lastPointer.x, y: lastPointer.y };
}

// ── Ausführung ─────────────────────────────────────────────────────────

/** Pending Rückbau-Timer des letzten "soft"-Laufs — muss beim nächsten Lauf
 *  abgebrochen werden, sonst räumt er mitten in einem neuen Durchlauf ab. */
let softFxTimer: number | null = null;
/** Das `settle` (u.a. `setTheme`) des noch laufenden "soft"-Timers. Bricht ein
 *  ueberlappender zweiter Lauf den Timer ab, MUSS dieses settle vorher noch
 *  feuern — sonst geht der Nachlauf des ersten Laufs verloren und der Modus
 *  wird nie persistiert. "soft" setzt `activeWave` nicht, also faengt die
 *  Doppelklick-Sperre der Aufrufer diesen Fall nicht ab. */
let softFxSettle: (() => void) | null = null;

/**
 * ECHTER Reveal ueber die View-Transitions-API.
 *
 * Der Browser haelt den alten Zustand als Bild fest und blendet den neuen
 * darueber ein — auf eine wachsende Form geclippt. Der Kreis IST dadurch schon
 * das neue Theme, mit Text, Rahmen und Logo. Das ist der Effekt aus dem ux-lab.
 *
 * Nichts kann hier auseinanderlaufen: Es gibt keinen Klon, dessen Zustand vom
 * Original abweichen koennte. Beide Bilder macht der Browser selbst.
 *
 * `mix-blend-mode: normal` ist noetig, sonst laeuft die Standard-Kreuzblende
 * zusaetzlich mit und die Kante wirkt matschig.
 *
 * Gibt false zurueck, wenn die API fehlt — dann uebernimmt der Farb-Crossfade.
 */
function runViewTransitionReveal(
  from: string,
  to: string,
  dur: number,
  apply: () => void,
  settle: () => void,
): boolean {
  const doc = document as Document & {
    startViewTransition?: (cb: () => Promise<void> | void) => { finished: Promise<void> };
  };
  if (!doc.startViewTransition) return false;

  const name = `jwReveal${Date.now().toString(36)}`;
  const style = document.createElement("style");
  style.textContent =
    `::view-transition-old(root){animation:none;mix-blend-mode:normal}` +
    `::view-transition-new(root){animation:${name} ${dur}ms cubic-bezier(0.4,0,0.2,1) both;` +
    `mix-blend-mode:normal;z-index:1}` +
    `@keyframes ${name}{from{clip-path:${from}}to{clip-path:${to}}}`;
  document.head.appendChild(style);

  // Auch hier gegen Doppelklick sperren — sonst startet ein zweiter Wechsel
  // mitten in der laufenden Transition. `settle` gehoert IN release: wird die
  // Transition von aussen ueber activeWave() abgebrochen, muss der Nachlauf
  // sofort laufen, nicht erst verzoegert ueber t.finished — sonst koennte ein
  // bereits committeter zweiter Wechsel danach vom verspaeteten setTheme des
  // ersten zurueckgekippt werden. settle ist idempotent, ein doppelter Aufruf
  // (Abbruch + regulaeres t.finished) ist damit harmlos.
  let entsperren: (() => void) | null = null;
  const release = () => {
    if (activeWave === release) activeWave = null;
    style.remove();
    settle();
    entsperren?.();
    entsperren = null;
  };
  activeWave = release;

  // Das NEUE Bild einer View-Transition ist live, nicht eingefroren: laufende
  // CSS-Uebergaenge der Seite spielen darin ab (s. `sperreUebergaenge`).
  const t = doc.startViewTransition(() => {
    entsperren = sperreUebergaenge();
    return anwendenImSchnappschuss(apply)();
  });
  void t.finished.catch(() => undefined).finally(release);
  return true;
}

let activeWave: (() => void) | null = null;

/** Laeuft gerade eine bewegte Transition (Desktop-View-Transition oder
 *  Mobil-Deckel)? Waehrenddessen darf nicht erneut umgeschaltet werden — ein
 *  Abbruch muesste die laufende hart auf ihr Ziel schnappen. */
export function isThemeTransitionBusy(): boolean {
  return activeWave !== null;
}

/** Setzt Stile einzeln statt per cssText: ein einziger nicht unterstuetzter Wert
 *  verwirft dort je nach CSS-Parser den GESAMTEN Block, und das Element staende
 *  voellig ohne Stil da (in der Testumgebung reproduziert). */
function applyStyles(el: HTMLElement, styles: Record<string, string>): void {
  for (const [k, v] of Object.entries(styles)) {
    (el.style as unknown as Record<string, string>)[k] = v;
  }
}

/** Mobil-Breakpoint — deckungsgleich mit der `@media (max-width: 768px)`-Regel
 *  in globals.css, die `html/body` auf `position: fixed` setzt. Genau dort
 *  verschiebt der Browser die View-Transition (per Messung belegt: Koordinaten
 *  korrekt, Viewport stabil, dennoch falsche Position — Compositing bei dpr>1),
 *  weshalb Mobil einen eigenen, snapshot-freien Pfad braucht. */
function isMobileViewport(): boolean {
  return typeof window !== "undefined" && window.innerWidth <= 768;
}

/**
 * Laeuft auf der Seite gerade ein Live-Video (Kamera, Bildschirmfreigabe)?
 *
 * Das entscheidet, ob ein Theme-Wechsel ueberhaupt mit Schnappschuessen
 * arbeiten darf. Beide schnappschussbasierten Wege zeigen Live-Video NICHT:
 *
 * - `runCoverReveal` (Mobil) klont den `<body>` per `cloneNode(true)`. Die
 *   Quelle eines Live-Videos haengt aber an der **Eigenschaft** `srcObject`,
 *   nicht an einem Attribut — `cloneNode` kopiert sie deshalb nicht. Im Klon
 *   sind alle Videokacheln leer.
 * - Die View-Transition legt ein Standbild ueber die Seite; Chromium
 *   komponiert Video auf Mobil haeufig auf einer eigenen Ebene, die in diesem
 *   Standbild fehlt.
 *
 * Gemeldet am 2026-08-18 aus einem Meeting am Handy: beim Umschalten von hell
 * auf dunkel verschwanden Kameras und Bildschirmfreigaben kurz. Fuer die halbe
 * Sekunde, die der Effekt laeuft, sah man leere Kacheln.
 *
 * Ausschlaggebend ist `srcObject`. Ein `<video src="…">` (Datei) ueberlebt den
 * Klon, weil `src` ein Attribut ist — nur der Medienstrom tut es nicht.
 */
function hasLiveVideo(): boolean {
  if (typeof document === "undefined") return false;
  for (const v of Array.from(document.getElementsByTagName("video"))) {
    if (!v.srcObject) continue;
    // Unsichtbare Elemente (vorgeladen, ausgeblendet) stoeren niemanden.
    if (v.offsetWidth === 0 && v.offsetHeight === 0) continue;
    return true;
  }
  return false;
}

// ── Weicher Uebergang ("soft") ──────────────────────────────────────────
// Bevorzugt ein GPU-Crossfade ueber die View-Transition (opacity ist
// compositor-beschleunigt → fluessig). Nur ohne die API der CSS-Farb-Uebergang
// als Fallback (transitioniert die Farbe JEDES Elements — reine Paint-Arbeit,
// ruckelt auf Mobil, weil pro Frame die halbe Seite neu gezeichnet wird).

/** Crossfade-Dauer (GPU-Pfad) — snappy, weil compositor. */
const SOFT_VT_MS = 300;
/** Dauer, nach der das Uebergangs-Attribut des CSS-Fallbacks wieder entfernt
 *  wird. Muss zur transition-duration in globals.css (400ms) passen, mit Puffer. */
const SOFT_MS = 460;
/** `transition` unter `html[data-theme-fx="soft"]` in globals.css — beide Werte gemeinsam aendern. */
const SOFT_CSS_MS = 400;
const SOFT_CSS_KURVE = "ease";

/** GPU-Crossfade: die neue Seite blendet per opacity ein, die alte bleibt
 *  statisch darunter. Kein clip-path, kein Ursprung → keine dpr>1-Verschiebung,
 *  laeuft daher auch auf Mobil sauber. Ueberschreibt die globale
 *  `::view-transition-*{animation:none}`-Regel nur fuer diesen Lauf. Gibt false
 *  zurueck, wenn die API fehlt. */
function runViewTransitionFade(dur: number, apply: () => void, settle: () => void): boolean {
  const doc = document as Document & {
    startViewTransition?: (cb: () => Promise<void> | void) => { finished: Promise<void> };
  };
  if (!doc.startViewTransition) return false;

  const style = document.createElement("style");
  style.textContent =
    `::view-transition-old(root){animation:none}` +
    `::view-transition-new(root){animation:jwSoftFade ${dur}ms ease both;mix-blend-mode:normal;z-index:1}` +
    `@keyframes jwSoftFade{from{opacity:0}to{opacity:1}}`;
  document.head.appendChild(style);

  let entsperren: (() => void) | null = null;
  const release = () => {
    if (activeWave === release) activeWave = null;
    style.remove();
    settle();
    entsperren?.();
    entsperren = null;
  };
  activeWave = release;

  // Das NEUE Bild einer View-Transition ist live, nicht eingefroren: laufende
  // CSS-Uebergaenge der Seite spielen darin ab (s. `sperreUebergaenge`).
  const t = doc.startViewTransition(() => {
    entsperren = sperreUebergaenge();
    return anwendenImSchnappschuss(apply)();
  });
  void t.finished.catch(() => undefined).finally(release);
  return true;
}

function runSoftFade(root: HTMLElement, apply: () => void, settle: () => void): void {
  // GPU-Crossfade wenn moeglich (fluessig); sonst CSS-Farb-Uebergang.
  // Live-Video kommt in keinen Schnappschuss (siehe `hasLiveVideo`) -- dann
  // bleibt nur der CSS-Farb-Uebergang, der ohne Schnappschuss auskommt.
  if (!hasLiveVideo() && runViewTransitionFade(SOFT_VT_MS, apply, settle)) return;

  // Ueberlappender zweiter Lauf: den Timer des ersten stoppen und dessen settle
  // (u.a. setTheme) nachholen, sonst geht der Nachlauf verloren und der Modus
  // wird nie persistiert. "soft" setzt activeWave NICHT, die Doppelklick-Sperre
  // der Aufrufer greift hier also nicht.
  if (softFxTimer !== null) {
    window.clearTimeout(softFxTimer);
    softFxSettle?.();
  }
  root.dataset.themeFx = "soft";
  apply();
  // Ohne Schnappschuss blendet jede Flaeche selbst — der Editor im iframe mit derselben
  // Dauer und Kurve wie die Regel in globals.css.
  void meldeThemaAnEinbettungen({ ms: SOFT_CSS_MS, kurve: SOFT_CSS_KURVE });
  softFxSettle = settle;
  softFxTimer = window.setTimeout(() => {
    delete root.dataset.themeFx;
    softFxTimer = null;
    softFxSettle = null;
    settle();
  }, SOFT_MS);
}

// ── Deckel-Reveal (Mobil) ────────────────────────────────────────────────
//
// WARUM KEINE VIEW-TRANSITION AUF MOBIL: Chromium verschiebt sie bei dpr>1
// (per Geraete-Messung belegt: clip-path-Koordinaten korrekt, Viewport stabil,
// Position dennoch falsch — in der Compositing-Ebene, fuer JS unerreichbar).
// `html, body` stehen dort auf `position: fixed` (globals.css, <=768px).
//
// WARUM DER DECKEL DEN ALTEN ZUSTAND ZEIGT UND NICHT DEN NEUEN: Bis 0.6xx lag
// ein Klon des NEUEN Zustands in einem wachsenden Kreis, eingefaerbt ueber die
// Basis-Variablen des Ziel-Themes an einem Wrapper. Shell 2.0 baut ihre Flaechen
// aber aus Variablen, die an `:root`/`html.dark` AUFGELOEST werden
// (`--jw-schale`, `--jw-tafel`, `--jw-thema-*`, inline von `applyThemeVars`) und
// als fertige Werte vererben — der Wrapper erreichte sie nicht. Gemessen am
// 2026-10-21 (ai-chat, 393x852): im Klon `--card`, `--jw-tafel`, Kartenfarbe
// rgb(255,255,255) beim Wechsel nach dunkel; der Kreis zeigte die ALTE Seite,
// nach ~600 ms sprang alles hart um. Den neuen Zustand vorab richtig zu malen
// hiesse, jede Ableitung der Huelle nachzubauen. Den ALTEN festzuhalten ist
// dagegen trivial: er steht gerade auf dem Bildschirm.
//
// Ablauf: (1) alle `--*` von <html> als AUFGELOESTE Werte lesen, <body> klonen,
// beides in einen Schatten-Baum ueber die Seite legen — der Deckel sieht exakt
// aus wie eben. (2) Unter dem Deckel sofort umschalten; React rendert verdeckt.
// (3) In den Deckel ein wachsendes Loch schneiden (clip-path, evenodd) — durch
// das Loch sieht man die ECHTE neue Seite, keine Kopie. Am Ende ist der Deckel
// ganz durchsichtig und faellt ohne Sprung weg.
//
// WARUM SCHATTEN-BAUM: unter dem Deckel traegt <html> schon den NEUEN Modus.
// Tailwinds `dark:` ist `&:is(.dark *)` — im Licht-Deckel unter `html.dark`
// griffen alle Dunkel-Varianten. Selektoren ueberqueren die Schattengrenze
// nicht; die Stile der Seite kommen als Kopie in den Schatten-Baum, die
// Variablen als festgehaltene Werte an den Wrapper.

const CLONE_ID = "jw-theme-clone";
const CLONE_SAMPLES = 32;

/** Uebertraegt Scrollpositionen, Formularwerte und Canvas-Inhalte, die
 *  cloneNode NICHT mitnimmt. Paarung per Index; beide Listen schliessen
 *  script/noscript aus, damit von Next.js mitten in den Body gestreamte
 *  `<script>__next_f`-Tags die Paarung nicht verschieben. */
function carryOverState(source: HTMLElement, clone: HTMLElement): void {
  const sel = ":not(script):not(noscript)";
  const a = source.querySelectorAll<HTMLElement>(sel);
  const b = clone.querySelectorAll<HTMLElement>(sel);
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const s = a[i];
    const c = b[i];
    if (s.scrollTop) c.scrollTop = s.scrollTop;
    if (s.scrollLeft) c.scrollLeft = s.scrollLeft;
    if (s instanceof HTMLInputElement && c instanceof HTMLInputElement) c.value = s.value;
    else if (s instanceof HTMLTextAreaElement && c instanceof HTMLTextAreaElement) c.value = s.value;
    else if (s instanceof HTMLCanvasElement && c instanceof HTMLCanvasElement && s.width && s.height) {
      try {
        c.getContext("2d")?.drawImage(s, 0, 0);
      } catch {
        /* WebGL ohne preserveDrawingBuffer / jsdom: bleibt leer */
      }
    }
  }
}

/** Was im Klon nicht neu laden oder abspielen darf: ein geklontes iframe laedt
 *  seine Quelle ein zweites Mal (und fuehrt deren Skripte aus), ein Video mit
 *  `autoplay` startet. Beides fuer ein Standbild von einer halben Sekunde. */
function entschaerfen(clone: HTMLElement): void {
  clone.querySelectorAll("script,noscript").forEach((el) => el.remove());
  clone.querySelectorAll("iframe").forEach((f) => {
    f.removeAttribute("src");
    f.removeAttribute("srcdoc");
  });
  clone.querySelectorAll("video,audio").forEach((m) => {
    m.removeAttribute("autoplay");
    m.setAttribute("preload", "none");
  });
}

/** Die Stile der Seite als EIN Text, fuer den Schatten-Baum. Gecacht ueber die
 *  Gestalt der Blattliste — sie aendert sich bei Navigation oder nachgeladenem
 *  CSS, nicht zwischen zwei Klicks auf den Modus-Schalter. Fremde Blaetter
 *  (Google Fonts) sind nicht lesbar und tragen nur @font-face, das ohnehin
 *  dokumentweit gilt. */
let stilCache: { key: string; text: string; sheet: CSSStyleSheet | null } | null = null;
function seitenStile(): { text: string; sheet: CSSStyleSheet | null } {
  const sheets = Array.from(document.styleSheets);
  const key = sheets
    .map((s) => {
      try {
        return `${s.href ?? "i"}:${s.cssRules.length}`;
      } catch {
        return "x";
      }
    })
    .join("|");
  if (stilCache?.key === key) return stilCache;
  const teile: string[] = [];
  for (const s of sheets) {
    try {
      for (const r of Array.from(s.cssRules)) teile.push(r.cssText);
    } catch {
      /* fremde Herkunft */
    }
  }
  const text = teile.join("\n");
  let sheet: CSSStyleSheet | null = null;
  try {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(text);
  } catch {
    sheet = null; // jsdom / alte Engines: <style> im Schatten-Baum
  }
  stilCache = { key, text, sheet };
  return stilCache;
}

/** Material-Standardkurve cubic-bezier(0.4, 0, 0.2, 1), nach x aufgeloest. */
function welleKurve(t: number): number {
  const bx = (u: number) => 3 * (1 - u) * (1 - u) * u * 0.4 + 3 * (1 - u) * u * u * 0.2 + u * u * u;
  const by = (u: number) => 3 * (1 - u) * u * u + u * u * u;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (bx(mid) < t) lo = mid;
    else hi = mid;
  }
  return by((lo + hi) / 2);
}

/** Deckflaeche mit kreisrundem Loch: Rechteck plus Kreis, evenodd. Gleiche
 *  Befehlsfolge fuer jeden Radius — so interpoliert der Browser zwischen den
 *  Stuetzstellen, statt zu springen. */
function lochPfad(w: number, h: number, x: number, y: number, r: number): string {
  const f = (n: number) => Math.round(n * 10) / 10;
  return (
    `path(evenodd, "M0 0H${f(w)}V${f(h)}H0Z` +
    `M${f(x - r)} ${f(y)}A${f(r)} ${f(r)} 0 1 0 ${f(x + r)} ${f(y)}` +
    `A${f(r)} ${f(r)} 0 1 0 ${f(x - r)} ${f(y)}Z")`
  );
}

const NO_TX_CSS = "*,*::before,*::after{transition:none!important}";

/** Unterdrueckt Farb-Uebergaenge der echten Seite. ai-chat traegt sie an ~200
 *  sichtbaren Elementen (`transition-colors`, 150–200 ms); ohne Sperre blenden
 *  sie nach dem Umschalten von ALT nach NEU — im Loch bzw. im Live-Bild der
 *  View-Transition sieht man dann z.B. das Eingabefeld noch hell auf dunkler
 *  Karte, und 200 gleichzeitige Farb-Animationen belegen den Hauptthread, der
 *  die clip-path-Animation treibt (gemessen 2026-10-21: Kreis stand ~80 ms und
 *  sprang dann). Gibt den Abbau zurueck — erst zwei Frames nach dem Nachlauf,
 *  damit dessen Re-Render (setTheme) auch noch ohne Uebergang landet. */
function sperreUebergaenge(): () => void {
  const el = document.createElement("style");
  el.setAttribute("data-jw-theme-notx", "");
  el.textContent = NO_TX_CSS;
  document.head.appendChild(el);
  return () => {
    requestAnimationFrame(() => requestAnimationFrame(() => el.remove()));
  };
}

function runCoverReveal(
  o: { x: number; y: number },
  apply: () => void,
  settle: () => void,
): void {
  // Aus der GERENDERTEN Viewport-Box, nicht aus innerWidth/innerHeight: die
  // Seite ist `height: 100dvh`, `innerHeight` weicht mobil um 1px ab.
  const rect = document.documentElement.getBoundingClientRect();
  const w = rect.width || window.innerWidth;
  const h = rect.height || window.innerHeight;
  const r = Math.hypot(Math.max(o.x, w - o.x), Math.max(o.y, h - o.y)) + 2;
  const dur = Math.round(Math.min(640, Math.max(420, 320 + r * 0.25)));

  // (1) Alten Zustand festhalten — VOR apply.
  const html = document.documentElement;
  const cs = getComputedStyle(html);
  const vars: [string, string][] = [];
  for (let i = 0; i < cs.length; i++) {
    const p = cs[i];
    if (p.startsWith("--")) vars.push([p, cs.getPropertyValue(p)]);
  }
  const metas = Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'));
  const alteLeiste = metas.map((m) => m.content);

  const host = document.createElement("div");
  host.id = CLONE_ID;
  host.setAttribute("aria-hidden", "true");
  applyStyles(host, {
    position: "fixed",
    left: "0px",
    top: "0px",
    width: `${w}px`,
    height: `${h}px`,
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: "2147483647",
    contain: "strict",
    clipPath: lochPfad(w, h, o.x, o.y, 0),
  });
  const root = host.attachShadow({ mode: "open" });
  const stile = seitenStile();
  const freeze = "*,*::before,*::after{animation:none!important;transition:none!important}";
  if (stile.sheet && "adoptedStyleSheets" in root) {
    const f = new CSSStyleSheet();
    f.replaceSync(freeze);
    root.adoptedStyleSheets = [stile.sheet, f];
  } else {
    const st = document.createElement("style");
    st.textContent = stile.text + "\n" + freeze;
    root.appendChild(st);
  }

  const wrap = document.createElement("div");
  wrap.className = html.className;
  for (const [k, v] of vars) wrap.style.setProperty(k, v);
  applyStyles(wrap, {
    position: "absolute",
    inset: "0px",
    overflow: "hidden",
    colorScheme: cs.colorScheme || "",
    backgroundColor: cs.backgroundColor,
    color: cs.color,
  });

  const clone = document.body.cloneNode(true) as HTMLElement;
  entschaerfen(clone);
  clone.setAttribute("inert", "");
  clone.style.width = `${w}px`;
  clone.style.height = `${h}px`;
  clone.style.margin = "0";
  wrap.appendChild(clone);
  root.appendChild(wrap);
  document.body.appendChild(host);
  carryOverState(document.body, clone);

  // (2) Darunter umschalten. Deckel und neue Seite gehen im SELBEN Frame an
  // den Bildschirm — es gibt keinen Frame, in dem die neue Seite offen liegt.
  const entsperren = sperreUebergaenge();
  anwendenSofort(apply);
  // Die Statusleiste haelt den alten Ton, bis das Loch den oberen Rand
  // erreicht — sonst kippt sie, waehrend darunter noch die alte Seite steht.
  const neueLeiste = metas.map((m) => m.content);
  metas.forEach((m, i) => {
    m.content = alteLeiste[i];
  });

  // (3) Loch auf. Erst nach zwei Frames: der synchrone Re-Render von apply()
  // und seine Effekte sind dann durch, der Hauptthread ist frei fuer die
  // Animation. Bis dahin deckt der Deckel unveraendert.
  const frames: Keyframe[] = [];
  let obenOffset = 1;
  for (let i = 0; i <= CLONE_SAMPLES; i++) {
    const t = i / CLONE_SAMPLES;
    const rr = r * welleKurve(t);
    if (obenOffset === 1 && rr >= o.y) obenOffset = t;
    frames.push({ clipPath: lochPfad(w, h, o.x, o.y, rr), offset: t });
  }

  let anim: Animation | null = null;
  let leisteTimer: number | null = null;
  let settled = false;
  const leisteSetzen = () => {
    metas.forEach((m, i) => {
      if (m.isConnected && m.content === alteLeiste[i]) m.content = neueLeiste[i];
    });
  };
  const finish = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(watchdog);
    if (leisteTimer !== null) window.clearTimeout(leisteTimer);
    leisteSetzen();
    anim?.cancel();
    host.remove();
    if (activeWave === finish) activeWave = null;
    settle();
    entsperren();
  };
  activeWave = finish;
  const watchdog = window.setTimeout(finish, dur + 2000);

  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      if (settled) return;
      if (typeof host.animate !== "function") {
        finish();
        return;
      }
      anim = host.animate(frames, { duration: dur, easing: "linear", fill: "forwards" });
      leisteTimer = window.setTimeout(leisteSetzen, Math.round(dur * obenOffset));
      anim.finished.then(finish, finish);
    }),
  );
}

/**
 * Führt `apply` aus und legt den gewählten Übergang darüber.
 *
 * `apply` MUSS den DOM synchron verändern (CSS-Variablen direkt schreiben,
 * nicht nur React-State setzen): startViewTransition schnappt den DOM im
 * Callback. Ein useEffect, der die Variablen erst nach dem Paint schreibt,
 * käme zu spät und die Transition zeigte zweimal denselben Zustand.
 */
export function runThemeTransition(
  fx: ThemeTransition,
  apply: () => void,
  /**
   * Auslösendes Element. Wenn vorhanden, ist ES der Ursprung — nicht der
   * globale Zeiger-Mitschnitt. Das ist der verlässliche Weg: in Menüs (Radix &
   * Co.) wird der Schalter teils über synthetische Events aktiviert, ohne dass
   * auf IHM ein pointerdown landet. Dann bliebe der letzte Mitschnitt stehen —
   * der vom Öffnen des Menüs — und die Welle liefe aus der falschen Ecke los.
   */
  originEl?: Element | null,
  /**
   * Laeuft ERST, wenn der Uebergang optisch durch ist.
   *
   * Hier gehoert alles hinein, was den Hauptthread beschaeftigt und optisch
   * nichts mehr beitraegt — allen voran `setTheme()` von next-themes. Die
   * clip-path-Animation der View-Transition wird vom Hauptthread getrieben;
   * laeuft eine Style-Neuberechnung des ganzen Dokuments hinein, verschluckt
   * sie ihre ersten Frames. Genau das war sichtbar: der Kreis erschien erst
   * auf halbem Weg.
   */
  onSettled?: () => void,
): void {
  const root = typeof document !== "undefined" ? document.documentElement : null;

  // Genau einmal — jeder Endpfad ruft es, mehrere duerfen sich ueberschneiden
  // (Abbruch von aussen + regulaeres Ende).
  let didSettle = false;
  const settle = () => {
    if (didSettle) return;
    didSettle = true;
    onSettled?.();
  };

  // Laufenden Wellen-Durchgang IMMER zuerst abschliessen — nicht erst in
  // runWaveOverlay. Sonst laeuft eine noch animierende Welle weiter, waehrend
  // ein Wechsel ueber einen anderen Pfad (keine/weich/diagonal, oder Welle ohne
  // Ursprung) sofort anwendet; ihr spaeteres apply() ueberschreibt dann den
  // frisch gesetzten Zustand mit seinem veralteten Ziel — und persistiert ihn
  // ueber setTheme() sogar nach Supabase. Ausloeser ist realistisch: Effekt-
  // Auswahl und Farbkacheln sitzen im selben Design-Tab.
  if (activeWave) activeWave();

  if (!root || fx === "none" || prefersReducedMotion()) {
    anwendenSofort(apply);
    settle();
    return;
  }

  // "soft" — reiner Farb-Crossfade, keine View-Transition. Details in runSoftFade.
  if (fx === "soft") {
    runSoftFade(root, apply, settle);
    return;
  }

  // KEINE Sperre fuer fremde oder sandboxed iframes mehr (bis 0.702.0 gab es sie): sie
  // schickte jede Seite mit offenem Jane-Panel in den CSS-Farbuebergang, und der lief am
  // Desktop in 1,4 s langen Aufgaben ohne ein Zwischenbild — Inhalt sofort neu, Rahmen ~1 s
  // alt, dann Sprung (tasks, 2026-10-23). Die Annahme dahinter, der Browser ueberspringe die
  // View-Transition still, ist im Bild widerlegt: fremdes und sandboxed iframe stehen im
  // Schnappschuss (Chromium 1223, Pixel mitten in der Transition), `ready` loest auf.

  // Laeuft ein Live-Video, faellt JEDER schnappschussbasierte Weg aus -- Welle,
  // Diagonale und der mobile Deckel-Reveal gleichermassen. `runSoftFade` merkt
  // dasselbe noch einmal und nimmt dann den CSS-Farb-Uebergang. Der Effekt ist
  // waehrend eines Gespraechs ohnehin die falsche Buehne; wichtiger ist, dass
  // die Gesichter stehen bleiben.
  if (hasLiveVideo()) {
    runSoftFade(root, apply, settle);
    return;
  }

  if (fx === "wave") {
    // MOBIL: Deckel-Reveal (s. `runCoverReveal`). Ursprung ist der Schalter —
    // in Shell 2.0 sitzt er im Kopf oben rechts oder im Fuss des Schubfachs;
    // das fruehere feste „unten-Mitte" stammte aus der alten unteren Leiste.
    // Ohne Schatten-DOM (sehr alte Engines) bleibt der Farb-Crossfade.
    if (isMobileViewport()) {
      if (typeof document.body.attachShadow !== "function") {
        runSoftFade(root, apply, settle);
        return;
      }
      const rect = document.documentElement.getBoundingClientRect();
      const cw = rect.width || window.innerWidth;
      const ch = rect.height || window.innerHeight;
      const o = elementOrigin(originEl) ?? freshPointer() ?? { x: cw / 2, y: ch };
      runCoverReveal({ x: Math.round(o.x), y: Math.round(o.y) }, apply, settle);
      return;
    }

    // DESKTOP: nativer View-Transition-Reveal von der Klickstelle — artefaktfrei.
    const w = window.innerWidth;
    const h = window.innerHeight;
    const o = elementOrigin(originEl) ?? freshPointer();
    if (!o) {
      anwendenSofort(apply);
      settle();
      return;
    }
    const r = Math.round(Math.hypot(Math.max(o.x, w - o.x), Math.max(o.y, h - o.y)) + 4);
    const x = Math.round(o.x);
    const y = Math.round(o.y);
    const dur = Math.round(Math.min(620, Math.max(360, 280 + r * 0.2)));
    if (
      !runViewTransitionReveal(
        `circle(0px at ${x}px ${y}px)`,
        `circle(${r}px at ${x}px ${y}px)`,
        dur,
        apply,
        settle,
      )
    ) {
      // Browser ohne View-Transitions-API (selten, alte Desktops): sauberer
      // Farb-Crossfade statt eines eigenen Overlays.
      runSoftFade(root, apply, settle);
    }
    return;
  }

  // Diagonale: Desktop UND Mobil ueber die View-Transition. Anders als die Welle
  // hat sie KEINEN festen Startpunkt — die Kante wischt von Ecke zu Ecke durch,
  // also faellt die dpr>1-Snapshot-Verschiebung, die den Welle-Kreis fehlplaziert
  // haette, hier nicht auf. Beide Keyframes brauchen dieselbe Punktzahl, sonst
  // interpoliert clip-path nicht und die Kante springt.
  const diagDur = Math.round(
    Math.min(620, Math.max(360, 280 + Math.hypot(window.innerWidth, window.innerHeight) * 0.16)),
  );
  if (
    !runViewTransitionReveal(
      "polygon(0% 0%, 0% 0%, -30% 100%, 0% 100%)",
      "polygon(0% 0%, 130% 0%, 100% 100%, 0% 100%)",
      diagDur,
      apply,
      settle,
    )
  ) {
    // Browser ohne View-Transitions-API: Farb-Crossfade (s. Welle-Fallback).
    runSoftFade(root, apply, settle);
  }
}
